import React, { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import DashboardIcon from '@mui/icons-material/Dashboard';
import AccountBalanceSharpIcon from '@mui/icons-material/AccountBalanceSharp';
import LeaderboardIcon from '@mui/icons-material/Leaderboard';
import PeopleIcon from '@mui/icons-material/People';
import AssignmentIcon from '@mui/icons-material/Assignment';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import MapIcon from '@mui/icons-material/Map';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import QuizIcon from '@mui/icons-material/Quiz';
import EmailIcon from '@mui/icons-material/Email';
import InsightsIcon from '@mui/icons-material/Insights';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useColors } from '../../theme/ColorModeContext';
import { getUserStats } from '../../api/Api';
import { academyTracks, getAcademyTrackForCategory } from '../Academy/skillTreeData';
import BrandLockup from '../../shared/BrandLockup';

// Collapsing the academy track list is how an admin reclaims the vertical space
// the Administration section needs: five tracks are around 200px, which is most
// of that section. Persisted so the choice survives a reload, and defaulted to
// open so nothing changes for anyone who never touches it.
const ACADEMY_NAV_KEY = 'academyNavOpen';

const readAcademyNavOpen = () => {
    try {
        return localStorage.getItem(ACADEMY_NAV_KEY) !== 'false';
    } catch {
        // private mode / blocked storage: fall back to the default
        return true;
    }
};

const rankToText = (rank: number) => {
    if (rank < 20) return "Novice";
    else if (rank < 40) return "Explorer";
    else if (rank < 60) return "Master";
    else if (rank < 80) return "Hacker";
    else if (rank < 100) return "Sentinel";
    else return "Overlord";
};

interface PropsNavbar {
    admin: boolean;
    smallMenu: boolean;
    setSmallMenu: any;
    token: string;
    username: string;
}

const Navbar = (props: PropsNavbar) => {
    const location = useLocation();
    const admin = props.admin;
    const smallMenu = props.smallMenu;
    const setSmallMenu = props.setSmallMenu;
    const token = props.token;
    const username = props.username;
    const colors = useColors();

    const rankColors: Record<string, string> = {
        novice: colors.rank.novice,
        explorer: colors.rank.explorer,
        master: colors.rank.master,
        hacker: colors.rank.hacker,
        sentinel: colors.rank.sentinel,
        overlord: colors.rank.overlord,
    };

    const sxLink = {
        textDecoration: "none",
        fontSize: "14px",
        fontWeight: 500,
        fontFamily: '"Exo 2", sans-serif',
        display: "flex",
        alignItems: "center",
        color: colors.text.secondary,
        padding: "12px 24px",
        transition: "all 0.2s ease",
        borderLeft: "3px solid transparent",
        "&:hover": {
            color: colors.text.primary,
            backgroundColor: colors.accent.cyanGlow,
        }
    };

    const sxLinkActive = {
        ...sxLink,
        color: colors.accent.cyan,
        backgroundColor: colors.accent.cyanGlow,
        borderLeft: `3px solid ${colors.accent.cyan}`,
        "&:hover": {
            color: colors.accent.cyan,
            backgroundColor: colors.accent.cyanGlow,
        }
    };

    const [rank, setRank] = useState("Novice");
    const [academyOpen, setAcademyOpen] = useState(readAcademyNavOpen);

    const toggleAcademy = () => {
        setAcademyOpen((open) => {
            const next = !open;
            try {
                localStorage.setItem(ACADEMY_NAV_KEY, String(next));
            } catch {
                // storage unavailable: the preference lasts for this session only
            }
            return next;
        });
    };

    useEffect(() => {
        const fetchStats = async () => {
            if (!token) return;
            try {
                const response = await getUserStats(token);
                if (response && response.data && response.data.total > 0) {
                    const data = response.data;
                    const tmpRank = rankToText(data.points * 100 / data.total);
                    setRank(tmpRank);
                }
            } catch {
                // silently ignore: backend may be unreachable
            }
        };
        fetchStats();
    }, [token]);

    const rankColor = rankColors[rank.toLowerCase()] || colors.text.secondary;
    const academyActive = location.pathname === "/academy" || location.pathname.startsWith("/academy/");
    const academyPathPart = location.pathname.split('/')[2];
    const activeAcademyTrack = academyTracks.some((track) => track.slug === academyPathPart)
        ? academyPathPart
        : academyPathPart && academyPathPart !== 'challenge'
            ? getAcademyTrackForCategory(academyPathPart).slug
            : '';

    return (
        <div style={{
            position: "fixed",
            backgroundColor: colors.bg.overlay,
            borderRight: `1px solid ${colors.border.subtle}`,
            height: '100vh',
            width: smallMenu ? '60px' : '220px',
            transition: 'width 0.3s ease',
            zIndex: 1100,
            display: 'flex',
            flexDirection: 'column',
            backdropFilter: 'blur(24px) saturate(1.8)',
            WebkitBackdropFilter: 'blur(24px) saturate(1.8)',
            boxShadow: '18px 0 48px rgba(0, 0, 0, 0.06)',
        }}>
            {/* Logo */}
            <Box sx={{
                height: smallMenu ? '56px' : '68px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderBottom: `1px solid ${colors.border.subtle}`,
                flexShrink: 0,
                padding: smallMenu ? '0' : '0 20px',
            }}>
                <BrandLockup compact={smallMenu} to="/dashboard" align="center" />
            </Box>

            {/* User Rank Section */}
            <Box sx={{
                padding: smallMenu ? '20px 8px' : '20px 24px',
                borderBottom: `1px solid ${colors.border.subtle}`,
                background: colors.gradient.cyanFade,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '8px',
                flexShrink: 0,
            }}>
                <Box sx={{
                    borderRadius: '50%',
                    padding: '2px',
                    border: `2px solid ${rankColor}`,
                    display: 'inline-block',
                }}>
                    <img
                        style={{
                            width: smallMenu ? '32px' : '64px',
                            height: smallMenu ? '32px' : '64px',
                            borderRadius: '50%',
                            display: 'block',
                            transition: 'width 0.3s ease, height 0.3s ease',
                        }}
                        src={`/images/ranks/${rank.toLowerCase()}.png`}
                        alt={rank}
                    />
                </Box>
                {!smallMenu && (
                    <>
                        <Typography sx={{
                            fontFamily: '"Orbitron", sans-serif',
                            fontWeight: 600,
                            fontSize: '14px',
                            color: colors.text.primary,
                            textAlign: 'center',
                        }}>
                            {username}
                        </Typography>
                        <Typography sx={{
                            fontFamily: '"Orbitron", sans-serif',
                            fontWeight: 500,
                            fontSize: '12px',
                            color: rankColor,
                            textTransform: 'uppercase',
                            letterSpacing: 0,
                        }}>
                            {rank}
                        </Typography>
                    </>
                )}
            </Box>

            {/* Everything between the rank block and the footer scrolls, so a short
                window can never put the tail of the Administration section out of
                reach. Scrollbar styling is global (see theme.ts). `minHeight: 0` is
                what actually lets a flex child shrink below its content height. */}
            <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden' }}>
            {/* Nav Items */}
            <ul style={{ padding: "0px", margin: "0px", listStyleType: "none" }}>
                <li style={{ margin: "0px", padding: "0px" }}>
                    <NavLink style={{ textDecoration: "none" }} to={"/dashboard"}>
                        <Box sx={location.pathname === "/dashboard" ? sxLinkActive : sxLink}>
                            <DashboardIcon sx={{ fontSize: "18px" }} />
                            {!smallMenu && <span style={{ marginLeft: "16px" }}>Dashboard</span>}
                        </Box>
                    </NavLink>
                </li>
                <li style={{ margin: "0px", padding: "0px" }}>
                    {/* Icon-only mode hides the track list anyway, so the row stays a
                        plain link there and only becomes a toggle when expanded. */}
                    {smallMenu ? (
                        <NavLink style={{ textDecoration: "none" }} to={"/academy"}>
                            <Box sx={academyActive ? sxLinkActive : sxLink}>
                                <AccountBalanceSharpIcon sx={{ fontSize: "18px" }} />
                            </Box>
                        </NavLink>
                    ) : (
                        <Box
                            role="button"
                            tabIndex={0}
                            aria-expanded={academyOpen}
                            title={academyOpen ? "Hide academy tracks" : "Show academy tracks"}
                            onClick={toggleAcademy}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter' || event.key === ' ') {
                                    event.preventDefault();
                                    toggleAcademy();
                                }
                            }}
                            sx={{
                                ...(academyActive ? sxLinkActive : sxLink),
                                cursor: 'pointer',
                                justifyContent: 'space-between',
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <AccountBalanceSharpIcon sx={{ fontSize: "18px" }} />
                                <span style={{ marginLeft: "16px" }}>Academy</span>
                            </Box>
                            {academyOpen
                                ? <ExpandLessIcon sx={{ fontSize: "18px" }} />
                                : <ExpandMoreIcon sx={{ fontSize: "18px" }} />}
                        </Box>
                    )}
                    {!smallMenu && academyOpen && (
                        <>
                            {academyTracks.map((track) => (
                                <NavLink key={track.slug} style={{ textDecoration: "none" }} to={`/academy/${track.slug}`}>
                                    <Box sx={{
                                        ...sxLink,
                                        padding: "10px 24px 10px 56px",
                                        fontSize: "13px",
                                        color: activeAcademyTrack === track.slug ? colors.accent.cyan : colors.text.secondary,
                                        backgroundColor: activeAcademyTrack === track.slug ? colors.accent.cyanGlow : "transparent",
                                        borderLeft: activeAcademyTrack === track.slug ? `3px solid ${colors.accent.cyan}` : "3px solid transparent",
                                        "&:hover": {
                                            color: colors.accent.cyan,
                                            backgroundColor: colors.accent.cyanGlow,
                                        }
                                    }}>
                                        {track.title}
                                    </Box>
                                </NavLink>
                            ))}
                        </>
                    )}
                </li>
                <li style={{ margin: "0px", padding: "0px" }}>
                    <NavLink style={{ textDecoration: "none" }} to={"/missions"}>
                        <Box sx={
                            location.pathname === "/missions" ||
                            location.pathname.startsWith("/missions/") ? sxLinkActive : sxLink}>
                            <MapIcon sx={{ fontSize: "18px" }} />
                            {!smallMenu && <span style={{ marginLeft: "16px" }}>Missions</span>}
                        </Box>
                    </NavLink>
                </li>
                <li style={{ margin: "0px", padding: "0px" }}>
                    <NavLink style={{ textDecoration: "none" }} to={"/leaderboard"}>
                        <Box sx={location.pathname === "/leaderboard" ? sxLinkActive : sxLink}>
                            <LeaderboardIcon sx={{ fontSize: "18px" }} />
                            {!smallMenu && <span style={{ marginLeft: "16px" }}>Leaderboard</span>}
                        </Box>
                    </NavLink>
                </li>
            </ul>
            {admin && <div>
                {!smallMenu && <h2 style={{
                    textAlign: "center",
                    fontFamily: '"Orbitron", sans-serif',
                    fontSize: "12px",
                    fontWeight: 600,
                    letterSpacing: 0,
                    textTransform: "uppercase",
                    color: colors.text.muted,
                    padding: "16px 24px 8px",
                }}>Administration</h2>}
                <ul style={{ padding: "0px", margin: "0px", listStyleType: "none" }}>
                    <li style={{ margin: "0px", padding: "0px" }}>
                        <NavLink style={{ textDecoration: "none" }} to={"/admin/intel"}>
                            <Box sx={location.pathname.startsWith("/admin/intel") ? sxLinkActive : sxLink}>
                                <InsightsIcon sx={{ fontSize: "18px" }} />
                                {!smallMenu && <span style={{ marginLeft: "16px" }}>User Intel</span>}
                            </Box>
                        </NavLink>
                    </li>
                    <li style={{ margin: "0px", padding: "0px" }}>
                        <NavLink style={{ textDecoration: "none" }} to={"/admin/challenges"}>
                            <Box sx={
                                location.pathname === "/admin/challenges" ||
                                location.pathname.startsWith("/admin/challenges/") ||
                                location.pathname.startsWith("/admin/challenge/") ? sxLinkActive : sxLink}>
                                <AssignmentIcon sx={{ fontSize: "18px" }} />
                                {!smallMenu && <span style={{ marginLeft: "16px" }}>Challenges</span>}
                            </Box>
                        </NavLink>
                    </li>
                    <li style={{ margin: "0px", padding: "0px" }}>
                        <NavLink style={{ textDecoration: "none" }} to={"/admin/theory/stages"}>
                            <Box sx={
                                location.pathname === "/admin/theory/stages" ||
                                location.pathname.startsWith("/admin/theory/") ? sxLinkActive : sxLink}>
                                <MenuBookIcon sx={{ fontSize: "18px" }} />
                                {!smallMenu && <span style={{ marginLeft: "16px" }}>Theory Stages</span>}
                            </Box>
                        </NavLink>
                    </li>
                    <li style={{ margin: "0px", padding: "0px" }}>
                        <NavLink style={{ textDecoration: "none" }} to={"/admin/users"}>
                            <Box sx={location.pathname === "/admin/users" ? sxLinkActive : sxLink}>
                                <PeopleIcon sx={{ fontSize: "18px" }} />
                                {!smallMenu && <span style={{ marginLeft: "16px" }}>Users</span>}
                            </Box>
                        </NavLink>
                    </li>
                    <li style={{ margin: "0px", padding: "0px" }}>
                        <NavLink style={{ textDecoration: "none" }} to={"/admin/emails"}>
                            <Box sx={location.pathname === "/admin/emails" ? sxLinkActive : sxLink}>
                                <EmailIcon sx={{ fontSize: "18px" }} />
                                {!smallMenu && <span style={{ marginLeft: "16px" }}>Campaigns</span>}
                            </Box>
                        </NavLink>
                    </li>
                    <li style={{ margin: "0px", padding: "0px" }}>
                        <NavLink style={{ textDecoration: "none" }} to={"/admin/tech-quizzes"}>
                            <Box sx={location.pathname === "/admin/tech-quizzes" ? sxLinkActive : sxLink}>
                                <QuizIcon sx={{ fontSize: "18px" }} />
                                {!smallMenu && <span style={{ marginLeft: "16px" }}>Tech Quizzes</span>}
                            </Box>
                        </NavLink>
                    </li>
                </ul>
            </div>}
            </Box>

            {/* Collapse Toggle */}
            <ul style={{
                padding: "0px",
                margin: "0px",
                listStyleType: "none",
                flexShrink: 0,
            }}>
                <li style={{ margin: "0px", padding: "0px", borderTop: `1px solid ${colors.border.subtle}` }}>
                    {!smallMenu ?
                        <Box sx={sxLink} onClick={() => setSmallMenu(true)}>
                            <CloseFullscreenIcon sx={{ fontSize: "18px" }} />
                            <span style={{ marginLeft: "16px" }}>Hide Menu</span>
                        </Box>
                        :
                        <Box sx={sxLink} onClick={() => setSmallMenu(false)}>
                            <FullscreenIcon sx={{ fontSize: "18px" }} />
                        </Box>
                    }
                </li>
            </ul>
        </div>
    );
}

export default Navbar;
